import React, { useState } from 'react';
import DeviceUsersService from '../../../../services/DeviceUsersService';
import MaterialTable, { MTableFilterRow } from 'material-table';
import Alert from '@material-ui/lab/Alert';
import {
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Snackbar,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { API_URL } from '../../../../utils/setApiUrl';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

function Attendance({
    tabName,
    deviceUsers,
    deviceMac,
    deviceId,
    deviceName,
    retrieveDeviceUsers,
}) {
    const { t } = useTranslation();
    const [loc] = React.useState(() => {
        const locData = localStorage.getItem('loc');
        return locData ? JSON.parse(locData).id : '';
    });
    const [iserror, setIserror] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const [selectedRows, setSelectedRows] = useState([]);

    const [snackbars, setSnackbars] = useState([]);

    const handleCloseSnackbar = id => {
        setSnackbars(prevSnackbars =>
            prevSnackbars.filter(snackbar => snackbar.id !== id)
        );
    };

    const handleShowSnackbar = (message, severity) => {
        const id = new Date().getTime(); // Unique ID for each snackbar
        const newSnackbar = {
            id,
            message,
            severity,
            position: snackbars.length * 55,
        };

        setSnackbars(prevSnackbars => [...prevSnackbars, newSnackbar]);
    };

    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [deallocateMessage, setDeallocationMessage] = useState('');
    const [confirmResendOpen, setConfirmResendOpen] = useState(false);
    const [resendProgress, setResendProgress] = useState('');

    const formatSkippedSummary = skippedByReason => {
        if (!skippedByReason) {
            return '';
        }

        const parts = [];
        if (skippedByReason['missing template']) {
            parts.push(
                t('devices.resendSkipMissingTemplate', {
                    count: skippedByReason['missing template'],
                })
            );
        }
        if (skippedByReason['missing user']) {
            parts.push(
                t('devices.resendSkipMissingUser', {
                    count: skippedByReason['missing user'],
                })
            );
        }
        if (skippedByReason['empty template data']) {
            parts.push(
                t('devices.resendSkipEmptyTemplate', {
                    count: skippedByReason['empty template data'],
                })
            );
        }
        if (skippedByReason['no fingerprint, pin, or card']) {
            parts.push(
                t('devices.resendSkipNoAuthData', {
                    count: skippedByReason['no fingerprint, pin, or card'],
                })
            );
        }
        return parts.join(', ');
    };

    const handleResendAllUsers = async () => {
        if (!deviceUsers.length) {
            handleShowSnackbar(t('devices.resendAllUsersEmpty'), 'warning');
            return;
        }

        setConfirmResendOpen(false);
        setIsResending(true);
        setResendProgress(
            t('devices.resendAllUsersInProgress', { device: deviceName })
        );

        try {
            const client = axios.create({
                baseURL: API_URL,
                timeout: 120000,
            });
            const response = await client.post('/resendAllUsersToDevice', {
                deviceId,
            });
            const {
                userCount,
                messageCount,
                totalRecords,
                skipped,
                skippedByReason,
                skippedDetails,
                skippedUsers,
                sentPinOrCardOnly,
                sentPinOrCardOnlyDetails,
                fullySynced,
            } = response.data;

            if (skippedDetails && skippedDetails.length) {
                console.warn('Resend all users skipped records:', {
                    skippedByReason,
                    skippedDetails,
                    skippedUsers,
                });
            }
            if (sentPinOrCardOnlyDetails && sentPinOrCardOnlyDetails.length) {
                console.info('Resend sent pin/card only (no fingerprint):', {
                    sentPinOrCardOnly,
                    sentPinOrCardOnlyDetails,
                });
            }

            if (messageCount === 0 && skipped > 0) {
                handleShowSnackbar(
                    t('devices.resendAllUsersPartialSuccessDetailed', {
                        recordsSent: messageCount,
                        totalRecords: totalRecords || skipped,
                        userCount,
                        device: deviceName,
                        skipped,
                        skippedSummary: formatSkippedSummary(skippedByReason),
                    }),
                    'warning'
                );
            } else if (messageCount === 0) {
                handleShowSnackbar(t('devices.resendAllUsersEmpty'), 'warning');
            } else if (!fullySynced && skipped > 0) {
                handleShowSnackbar(
                    t('devices.resendAllUsersPartialSuccessDetailed', {
                        recordsSent: messageCount,
                        totalRecords: totalRecords || messageCount + skipped,
                        userCount,
                        device: deviceName,
                        skipped,
                        skippedSummary: formatSkippedSummary(skippedByReason),
                    }),
                    'warning'
                );
            } else {
                const successKey =
                    sentPinOrCardOnly > 0
                        ? 'devices.resendAllUsersSuccessWithPinOrCard'
                        : 'devices.resendAllUsersSuccessDetailed';
                handleShowSnackbar(
                    t(successKey, {
                        count: userCount,
                        recordsSent: messageCount,
                        device: deviceName,
                        pinOrCardCount: sentPinOrCardOnly || 0,
                    }),
                    'success'
                );
            }

            await retrieveDeviceUsers();
        } catch (error) {
            console.error('Resend all users error:', error);
            handleShowSnackbar(
                t('devices.resendAllUsersError', { device: deviceName }),
                'error'
            );
        } finally {
            setIsResending(false);
            setResendProgress('');
        }
    };

    const handleRemoveUserFromDevice = async (chosenUserSap, resolve) => {
        setIsLoading(true);
        handleShowSnackbar(
            t('devices.removeUserFromDevice', {
                name: chosenUserSap,
                device: deviceName,
            }),
            'info'
        );

        try {
            const client = axios.create({ baseURL: API_URL, timeout: 10000 });
            const device = deviceMac;

            const response = await client.post('/deallocate', {
                user: JSON.stringify(chosenUserSap),
                device,
            });

            const { status } = response;

            if (status === 200) {
                const { status } = await DeviceUsersService.removeFromDevice(
                    chosenUserSap,
                    deviceId
                );

                if (status === 200) {
                    resolve();
                    handleShowSnackbar(
                        t('devices.removeUserFromDeviceSuccess', {
                            name: chosenUserSap,
                            device: deviceName,
                        }),
                        'success'
                    );
                    await new Promise(resolve => {
                        setTimeout(async () => {
                            await retrieveDeviceUsers(); // Call the retrieveDeviceUsers function after a delay of 5 seconds
                            resolve();
                        }, 3000);
                    });
                } else {
                    resolve();
                    handleShowSnackbar(
                        t('devices.removeUserFromDeviceError', {
                            name: chosenUserSap,
                            device: deviceName,
                        }),
                        'error'
                    );
                }
            } else {
                resolve();
                handleShowSnackbar(
                    t('devices.removeUserFromDeviceError', {
                        name: chosenUserSap,
                        device: deviceName,
                    }),
                    'error'
                );
            }
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Request canceled:', error.message);
            } else if (error.code === 'ECONNABORTED') {
                resolve();
                handleShowSnackbar(
                    t('devices.removeUserFromDeviceError', {
                        name: chosenUserSap,
                        device: deviceName,
                    }),
                    'error'
                );
            } else {
                console.error(error);
            }
        }
    };

    const columns = [
        {
            title: t('devices.employee'),
            field: 'userInDevice.name',
        },
        {
            title: t('devices.employeeID'),
            field: 'userInDevice.sap',
        },
    ];

    const components = {
        FilterRow: props => <MTableFilterRow {...props} />,
    };

    // Debug log to check incoming data
    React.useEffect(() => {
        console.log('Attendance component received deviceUsers:', deviceUsers);
        console.log(
            'Number of users:',
            (deviceUsers && deviceUsers.length) || 0
        );
        console.log('For device:', deviceName, deviceId);
        console.log('Sample user data:', deviceUsers && deviceUsers[0]);
    }, [deviceUsers, deviceName, deviceId]);

    return (
        <div>
            {snackbars
                .slice()
                .reverse()
                .map((snackbar, index) => (
                    <Snackbar
                        key={snackbar.id}
                        open={true}
                        autoHideDuration={5000}
                        onClose={() => handleCloseSnackbar(snackbar.id)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        style={{ bottom: `${10 + index * 55}px` }}
                    >
                        <Alert
                            elevation={6}
                            variant="filled"
                            severity={snackbar.severity}
                            onClose={() => handleCloseSnackbar(snackbar.id)}
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>
                ))}

            <Backdrop
                open={isResending}
                style={{ zIndex: 1200, color: '#fff', flexDirection: 'column' }}
            >
                <CircularProgress color="inherit" />
                {resendProgress && (
                    <Box mt={2} textAlign="center" px={2}>
                        {resendProgress}
                    </Box>
                )}
            </Backdrop>

            <Box px={2} pb={1} display="flex" justifyContent="flex-end">
                <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<SyncIcon />}
                    disabled={isResending || !deviceUsers.length}
                    onClick={() => setConfirmResendOpen(true)}
                >
                    {t('devices.resendAllUsers')}
                </Button>
            </Box>

            <Dialog
                open={confirmResendOpen}
                onClose={() => setConfirmResendOpen(false)}
            >
                <DialogTitle>{t('devices.resendAllUsers')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {t('devices.resendAllUsersConfirmation', {
                            count: deviceUsers.length,
                            device: deviceName,
                        })}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmResendOpen(false)}>
                        {t('devices.common.cancel')}
                    </Button>
                    <Button
                        onClick={handleResendAllUsers}
                        color="primary"
                        variant="contained"
                        autoFocus
                    >
                        {t('devices.resendAllUsers')}
                    </Button>
                </DialogActions>
            </Dialog>

            <MaterialTable
                columns={columns}
                data={deviceUsers}
                style={{
                    borderRadius: '0',
                    boxShadow: 'none',
                    padding: '0px !important',
                }}
                options={{
                    loadingType: 'linear',
                    pageSize: 50,
                    maxBodyHeight: '67vh',
                    showTitle: false,
                    toolbar: false,
                    sorting: true,
                    search: false,
                    actionsColumnIndex: -1,
                    exportButton: false,
                    filtering: true,
                    pageSizeOptions: [10, 20, 50, 100, 200, 300, 400, 500],
                }}
                components={components}
                onSelectionChange={rows => {
                    setSelectedRows(rows);
                }}
                editable={{
                    onRowDelete: ChosenUser =>
                        new Promise(resolve => {
                            handleRemoveUserFromDevice(
                                ChosenUser.userInDevice.sap,
                                resolve
                            );
                        }),
                }}
                localization={{
                    pagination: {
                        labelDisplayedRows:
                            '{from}-{to} ' +
                            t('reports.common.of') +
                            ' {count}',
                        labelRowsPerPage: t('reports.common.rowsPerPage'),
                        labelRowsSelect: t('reports.common.rowsSelect'),
                    },
                    header: {
                        actions: t('reports.common.actions'),
                    },
                    body: {
                        emptyDataSourceMessage: t(
                            'reports.common.emptyDataSourceMessage'
                        ),
                        editTooltip: t('reports.common.edit'),
                        deleteTooltip: t('reports.common.delete'),
                        filterRow: {
                            filterTooltip: t('reports.common.filter'),
                        },
                        editRow: {
                            deleteText: t('devices.common.deleteConfirmation'),
                            cancelTooltip: t('devices.common.cancel'),
                            saveTooltip: t('devices.common.save'),
                        },
                    },
                    toolbar: {
                        exportTitle: t('reports.common.export'),
                        exportName: t('reports.common.exportCSV'),
                        searchTooltip: t('reports.common.search'),
                        searchPlaceholder: t(
                            'reports.common.searchPlaceholder'
                        ),
                        nRowsSelected: t('reports.common.rowsSelected', {
                            count: selectedRows.length,
                        }),
                    },
                }}
            />
        </div>
    );
}

export default Attendance;
